import { computed, type ComputedRef } from 'vue'
import { storeToRefs } from 'pinia'
import { useMediaQuery } from '@vueuse/core'
import { MediaQuery } from '@cplug/design-system/components/props'
import useAssistantShellStore from '../stores/useAssistantShellStore'
import { useAssistantChat } from './useAssistantChat'
import useMenuSidebarStore from '~/shared/stores/useMenuSidebarStore'

/** Largura do assistente em desktops largos (≥ breakpoint wide). */
export const ASSISTANT_SIDEBAR_WIDTH_WIDE_PX = 400

/** Acima de 1280px o layout empurra; abaixo de 1536px o assistente usa a largura do menu. */
export const ASSISTANT_SIDEBAR_WIDE_MEDIA_QUERY = '(min-width: 1536px)'

/** Largura base do menu lateral (MenuSidebar core). */
export const ASSISTANT_SIDEBAR_WIDTH_COMPACT_MENU_BASE_PX = 276

/** Folga extra no modo compacto — largura final alinha com wide (400px) para o chat respirar. */
export const ASSISTANT_SIDEBAR_WIDTH_COMPACT_EXTRA_PX = 124

/** Fallback quando a store do menu ainda não sincronizou. */
export const ASSISTANT_SIDEBAR_WIDTH_COMPACT_FALLBACK_PX =
  ASSISTANT_SIDEBAR_WIDTH_COMPACT_MENU_BASE_PX + ASSISTANT_SIDEBAR_WIDTH_COMPACT_EXTRA_PX

interface UseAssistantSidebarReturn {
  isAssistantEnabled: ComputedRef<boolean>
  showAssistantComingSoon: ComputedRef<boolean>
  isAssistantOpen: ComputedRef<boolean>
  isDesktopShell: ComputedRef<boolean>
  /** Desktop compacto (1280–1535): assistente aberto esconde o menu lateral. */
  shouldHideMenuForAssistant: ComputedRef<boolean>
  rightSidebarWidth: ComputedRef<number>
  rightSidebarOverlay: ComputedRef<boolean>
  openAssistantSidebar: () => void
  closeAssistantSidebar: () => void
  toggleAssistantSidebar: () => void
}

function getCompactAssistantSidebarWidth (menuWidth: number): number {
  const baseMenuWidth = menuWidth >= ASSISTANT_SIDEBAR_WIDTH_COMPACT_MENU_BASE_PX
    ? menuWidth
    : ASSISTANT_SIDEBAR_WIDTH_COMPACT_MENU_BASE_PX

  return baseMenuWidth + ASSISTANT_SIDEBAR_WIDTH_COMPACT_EXTRA_PX
}

export function useAssistantSidebar (): UseAssistantSidebarReturn {
  const { isAssistantEnabled, showAssistantComingSoon } = useAssistantChat()
  const shellStore = useAssistantShellStore()
  const menuSidebarStore = useMenuSidebarStore()
  const { isOpen } = storeToRefs(shellStore)
  const { totalSidebarWidth } = storeToRefs(menuSidebarStore)
  const isDesktopShellQuery = useMediaQuery(MediaQuery.DesktopWithSidebar)
  const isWideDesktopQuery = useMediaQuery(ASSISTANT_SIDEBAR_WIDE_MEDIA_QUERY)

  const isAssistantOpen = computed(() =>
    isAssistantEnabled.value && Boolean(isOpen.value)
  )

  const isDesktopShell = computed(() => isDesktopShellQuery.value)

  const rightSidebarWidth = computed(() => {
    if (!isAssistantOpen.value || !isDesktopShell.value) {
      return 0
    }

    if (isWideDesktopQuery.value) {
      return ASSISTANT_SIDEBAR_WIDTH_WIDE_PX
    }

    return getCompactAssistantSidebarWidth(totalSidebarWidth.value)
  })

  const rightSidebarOverlay = computed(() =>
    isAssistantOpen.value && !isDesktopShell.value
  )

  /** Só esconde menu no push compacto; em ≥1536px menu + assistente coexistem. */
  const shouldHideMenuForAssistant = computed(() =>
    isAssistantOpen.value && isDesktopShell.value && !isWideDesktopQuery.value
  )

  function openAssistantSidebar (): void {
    shellStore.open()
  }

  function closeAssistantSidebar (): void {
    shellStore.close()
  }

  function toggleAssistantSidebar (): void {
    shellStore.toggle()
  }

  return {
    isAssistantEnabled,
    showAssistantComingSoon,
    isAssistantOpen,
    isDesktopShell,
    shouldHideMenuForAssistant,
    rightSidebarWidth,
    rightSidebarOverlay,
    openAssistantSidebar,
    closeAssistantSidebar,
    toggleAssistantSidebar
  }
}
